/**
 * AI Agent Service for interacting with Gemini and other AI models
 * Uses environment variables VITE_GEMINI_API_KEY and MODEL_API_KEY
 */

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const MODEL_API_KEY = import.meta.env.MODEL_API_KEY;
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent';

export interface AIResponse {
  text: string;
  success: boolean;
  error?: string;
}

export interface ContentRequest {
  prompt: string;
  model?: 'gemini' | 'custom';
}

/**
 * Generate content using Google's Gemini API
 */
export async function generateWithGemini(prompt: string): Promise<AIResponse> {
  if (!GEMINI_API_KEY) {
    console.warn('VITE_GEMINI_API_KEY is not configured. AI features will be limited.');
    return {
      text: "AI features are currently unavailable. Please configure the API key.",
      success: false,
      error: 'Missing API key'
    };
  }

  try {
    const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: prompt
          }]
        }]
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to fetch from Gemini');
    }

    const data = await response.json();
    const text = data.candidates?.[0]?.content?.parts?.[0]?.text || 'No response generated';

    return {
      text,
      success: true
    };
  } catch (error) {
    console.error('Gemini API error:', error);
    return {
      text: 'Sorry, I encountered an error while processing your request.',
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Generate content using a custom AI model (configured via MODEL_API_KEY)
 */
export async function generateWithCustomModel(prompt: string): Promise<AIResponse> {
  if (!MODEL_API_KEY) {
    console.warn('MODEL_API_KEY is not configured. Using fallback response.');
    return {
      text: "Custom AI model is not configured. Please set MODEL_API_KEY environment variable.",
      success: false,
      error: 'Missing API key'
    };
  }

  try {
    // Replace with your actual custom model API endpoint
    const CUSTOM_API_URL = 'https://api.your-custom-model.com/v1/generate';
    
    const response = await fetch(CUSTOM_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${MODEL_API_KEY}`
      },
      body: JSON.stringify({
        prompt: prompt,
        max_tokens: 1000,
        temperature: 0.7
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch from custom model');
    }

    const data = await response.json();
    // Adjust based on your custom model's response structure
    const text = data.response || data.text || data.choices?.[0]?.text || 'No response generated';

    return {
      text,
      success: true
    };
  } catch (error) {
    console.error('Custom Model API error:', error);
    return {
      text: 'Sorry, I encountered an error while processing your request.',
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Main AI agent function that routes to appropriate model
 */
export async function AIAgent(request: ContentRequest): Promise<AIResponse> {
  const { prompt, model = 'gemini' } = request;

  if (!prompt || prompt.trim().length === 0) {
    return {
      text: 'Please provide a valid prompt.',
      success: false,
      error: 'Empty prompt'
    };
  }

  switch (model) {
    case 'gemini':
      return generateWithGemini(prompt);
    case 'custom':
      return generateWithCustomModel(prompt);
    default:
      return generateWithGemini(prompt);
  }
}

/**
 * Generate SEO-optimized content for the web app
 */
export async function generateSEOContent(topic: string): Promise<AIResponse> {
  const prompt = `Generate SEO-optimized, engaging, and catchy content about "${topic}". 
  Include relevant keywords, a compelling call-to-action, and make it suitable for a monetized web app.
  Keep it concise (under 200 words) and highly engaging.`;
  
  return AIAgent({ prompt, model: 'gemini' });
}

/**
 * Generate random interesting facts for user engagement
 */
export async function generateInterestingFact(): Promise<AIResponse> {
  const prompt = "Generate a mind-blowing, drop-dead interesting fact that will captivate website visitors. Make it surprising and shareable.";
  
  return AIAgent({ prompt, model: 'gemini' });
}
